'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { reduxLogger } from '@/core/utils/logger';
import { AlertCircle, RefreshCw, Check } from 'lucide-react';
import { reportReactError } from '@/shared/utils/errorReporting';
import { config } from '@/config/config';
import { getSentryUserContext } from '@/shared/utils/sentryContext';

const MAX_RETRIES = 3;

interface Props {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo, retry: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  minimal?: boolean; // Use minimal UI without Sentry integration
  showDetails?: boolean; // Show error details (development only, ignored in production)
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
  retryCount: number;
  reported: boolean;
  reporting: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      retryCount: 0,
      reported: false,
      reporting: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Always log to console in development
    if (config.features.debugLogging) {
      reduxLogger.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Skip Sentry for minimal mode
    if (this.props.minimal) {
      return;
    }

    // Report to error tracking
    reportReactError(error, errorInfo, {
      context: 'error_boundary',
      extra: {
        timestamp: new Date().toISOString()
      }
    });

    // Report to Sentry in production/staging (no-ops if Sentry.init() not called)
    if (config.env === 'production' || config.env === 'staging') {
      const userContext = getSentryUserContext();
      Sentry.withScope((scope) => {
        if (userContext.userId) scope.setTag('user_id', userContext.userId);
        if (userContext.organizationId) scope.setTag('organization_id', userContext.organizationId);
        const eventId = Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            },
          },
        });
        this.setState({ eventId: eventId || null });
      });
    }
  }

  handleRetry = () => {
    const newCount = this.state.retryCount + 1;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
      retryCount: newCount,
      reported: false,
      reporting: false,
    });
  };

  handleReportIssue = () => {
    this.setState({ reporting: true });
    try {
      const userContext = getSentryUserContext();
      Sentry.captureFeedback({
        message: [
          'User-reported error boundary crash.',
          `Error: ${this.state.error?.message || 'Unknown error'}`,
          `Component: ${this.state.errorInfo?.componentStack?.split('\n')[1]?.trim() || 'N/A'}`,
          userContext.userId ? `User: ${userContext.userId}` : '',
          userContext.organizationId ? `Org: ${userContext.organizationId}` : '',
        ].filter(Boolean).join('\n'),
        associatedEventId: this.state.eventId || undefined,
      });
      this.setState({ reported: true, reporting: false });
    } catch {
      // The error was already captured by Sentry in componentDidCatch
      this.setState({ reported: true, reporting: false });
    }
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo ?? { componentStack: '' },
          this.handleRetry
        );
      }

      // Minimal fallback UI
      if (this.props.minimal) {
        return (
          <div className="p-4 rounded-md bg-red-50 border border-red-200 m-4">
            <h2 className="text-lg font-medium text-red-800">Something went wrong</h2>
            <p className="mt-2 text-sm text-red-700">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={this.handleRetry}
              className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-md text-sm font-medium"
            >
              Try again
            </button>
          </div>
        );
      }

      // Full fallback UI
      const retriesExhausted = this.state.retryCount >= MAX_RETRIES;

      return (
        <div className="min-h-screen flex items-center justify-center p-4" data-testid="error-boundary">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Something went wrong
            </h2>
            <p className="mb-6" style={{ color: 'var(--color-text-secondary)' }}>
              {retriesExhausted
                ? 'This issue persists. Please refresh the page or report the issue below.'
                : 'We apologize for the inconvenience. Please refresh the page or try again later.'}
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-md font-medium transition-colors inline-flex items-center gap-2"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'white'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh Page
              </button>
              <button
                onClick={this.handleRetry}
                disabled={retriesExhausted}
                className="px-4 py-2 rounded-md font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  color: retriesExhausted ? 'var(--color-text-tertiary, #9CA3AF)' : 'var(--color-text-primary)',
                  border: '1px solid var(--color-border)',
                  cursor: retriesExhausted ? 'not-allowed' : 'pointer',
                  opacity: retriesExhausted ? 0.5 : 1,
                }}
                data-testid="error-retry-button"
              >
                Try Again
              </button>
              {this.state.reported ? (
                <button
                  disabled
                  className="px-4 py-2 rounded-md font-medium transition-colors inline-flex items-center gap-2"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-success, #22C55E)',
                    border: '1px solid var(--color-success, #22C55E)',
                  }}
                >
                  <Check className="h-4 w-4" />
                  Issue Reported
                </button>
              ) : (
                <button
                  onClick={this.handleReportIssue}
                  disabled={this.state.reporting}
                  className="px-4 py-2 rounded-md font-medium transition-colors"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border)'
                  }}
                >
                  {this.state.reporting ? 'Reporting...' : 'Report Issue'}
                </button>
              )}
            </div>
            {this.state.reported && (
              <p className="mt-3 text-sm" style={{ color: 'var(--color-success, #22C55E)' }}>
                Thank you. Our team has been notified and will investigate this issue.
              </p>
            )}
            {/* Stack traces NEVER shown in production, regardless of debug flags */}
            {config.env !== 'production' && (config.features.debugLogging || this.props.showDetails) && (
              <details className="mt-6 text-left p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
                <summary className="cursor-pointer font-semibold text-sm">
                  Error Details
                </summary>
                <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap">
                  {this.state.error.stack}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * MinimalErrorBoundary - Lightweight error boundary for simple use cases
 *
 * Uses ErrorBoundary with `minimal` mode for a compact error UI.
 * Accepts an optional static fallback ReactNode (not a function).
 */
export const MinimalErrorBoundary: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => {
  return (
    <ErrorBoundary
      minimal
      fallback={fallback ? () => fallback : undefined}
    >
      {children}
    </ErrorBoundary>
  );
};

// Default export
export default ErrorBoundary;
